import React, { ReactNode, useRef, useEffect } from "react";
import {
  View,
  StyleSheet,
  Animated,
  Easing,
  Platform,
} from "react-native";
import { useThemeSettings } from "../ThemeProvider";
import Svg, { Defs, RadialGradient, Rect, Stop } from "react-native-svg";

interface RadialRingsProps {
  children?: ReactNode;
  size?: number;
  rings?: number;
}

export function RadialGradientBox({
  children,
  size = 110,
  rings = 3,
}: RadialRingsProps) {
  const { theme } = useThemeSettings();

  const animationsRef = useRef(
    Array.from({ length: rings }, () => new Animated.Value(0))
  );
  const animations = animationsRef.current;

  const gradientId = useRef(`radial-${Date.now()}`).current;

  const useNative = Platform.OS !== "web";
  const timers = useRef<number[]>([]);

  useEffect(() => {
    // --- WEB STRATEGY (recursive restart) ---
    if (!useNative) {
      const runs = animations.map((anim) => {
        const run = () => {
          anim.setValue(0);

          Animated.timing(anim, {
            toValue: 1,
            duration: 2400,
            easing: Easing.out(Easing.ease),
            useNativeDriver: false,
            isInteraction: false,
          }).start(run);
        };

        return run;
      });

      runs.forEach((run, i) => {
        timers.current[i] = setTimeout(run, i * 600);
      });

      return () => {
        timers.current.forEach(clearTimeout);
      };
    }

    // --- NATIVE STRATEGY ---
    const loops = animations.map((anim) => {
      anim.setValue(0);

      return Animated.loop(
        Animated.timing(anim, {
          toValue: 1,
          duration: 2400,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
          isInteraction: false,
        }),
        { resetBeforeIteration: true }
      );
    });

    loops.forEach((loop, i) => {
      timers.current[i] = setTimeout(() => loop.start(), i * 600);
    });

    return () => {
      timers.current.forEach(clearTimeout);
      loops.forEach((l) => l.stop());
    };
  }, []);

  return (
    <View style={[styles.wrapper, { width: size, height: size }]}>
      {/* RINGS */}
      {animations.map((anim, i) => {
        const scale = anim.interpolate({
          inputRange: [0, 1],
          outputRange: [0.7, 1.2],
        });

        const opacity = anim.interpolate({
          inputRange: [0, 0.7, 1],
          outputRange: [0.25, 0.12, 0],
        });

        return (
          <Animated.View
            key={i}
            style={[
              styles.ring,
              {
                borderColor: "#F2BB05",
                width: size,
                height: size,
                opacity,
                transform: [{ scale }],
              },
            ]}
          />
        );
      })}

      {/* GRADIENT */}
      <View style={{ alignSelf: "center" }}>
        <Svg
          style={[
            StyleSheet.absoluteFill,
            { left: -38, height: size, width: size },
          ]}
          pointerEvents="none"
        >
          <Defs>
            <RadialGradient
              id={gradientId}
              cx="50%"
              cy="50%"
              rx="50%"
              ry="50%"
              fx="50%"
              fy="50%"
            >
              <Stop
                offset="0%"
                stopColor={theme?.colors?.footerGradient1}
              />
              <Stop
                offset="100%"
                stopColor={theme?.colors?.footerGradient2}
              />
            </RadialGradient>
          </Defs>

          <Rect
            x={0}
            y={0}
            width={size * 0.7}
            height={size * 0.7}
            rx={(size * 0.7) / 2}
            ry={(size * 0.7) / 2}
            fill={`url(#${gradientId})`}
          />
        </Svg>
      </View>

      {/* CENTER */}
      <View
        style={[
          styles.center,
          {
            width: size * 0.7,
            height: size * 0.7,
          },
        ]}
      >
        {children}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
  },

  ring: {
    position: "absolute",
    borderRadius: 999,
    borderWidth: 10,
  },

  center: {
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",

    shadowColor: "#000",
    shadowOpacity: 0.25,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 4 },
  },
});
